import React, { useState, useEffect } from 'react';
import { WhatsAppOutlined,ClockCircleOutlined, InfoCircleOutlined } from '@ant-design/icons';
import { searchRoutes } from '../../api/routes';
import { RouteStatus } from '@dev-zeromoblt/o-platform-client';
import './styles/RouteInfoTile.css';

interface RouteInfoTileProps {
  studentId: string;
  variant?: 'banner' | 'card';
}

export const RouteInfoTile: React.FC<RouteInfoTileProps> = ({ studentId, variant = 'banner' }) => {
  const [loading, setLoading] = useState(true);
  const [routes, setRoutes] = useState<any[]>([]);

  useEffect(() => {
    const fetchRoute = async () => {
      try {
        setLoading(true);
        const result = await searchRoutes(`@group:{${studentId}}`, 0, 10);
        const fetchedRoutes = result?.routes || [];
        setRoutes(fetchedRoutes);
      } catch (error) {
        console.error('[RouteInfoTile] Error fetching route:', error);
      } finally {
        setLoading(false);
      }
    };

    if (studentId) {
      fetchRoute();
    }
  }, [studentId]);

  const PLANNING_STAGES = [RouteStatus.PLANNING, RouteStatus.PENDING_APPROVAL];

  // Categorize routes by status
  const activeRoutes = routes.filter(r => !PLANNING_STAGES.includes(r.status) && r.status !== RouteStatus.INACTIVE);
  const planningRoutes = routes.filter(r => PLANNING_STAGES.includes(r.status));
  const inactiveRoutes = routes.filter(r => r.status === RouteStatus.INACTIVE);

  const hasActiveRoutes = activeRoutes.length > 0;
  const hasPlanningRoutes = planningRoutes.length > 0;
  const hasInactiveRoutes = inactiveRoutes.length > 0;

  // Card variant for subscription details page
  if (variant === 'card') {
    // Show skeleton loading for card variant
    if (loading) {
      return (
        <div className="route-info-tile route-info-tile--card route-info-tile--loading">
          <div className="route-info-tile__skeleton-badge"></div>
          <div className="route-info-tile__skeleton-title"></div>
          <div className="route-info-tile__skeleton-button"></div>
        </div>
      );
    }

    // Don't show card if no routes found
    if (routes.length === 0) {
      return null;
    }

    // Render a component for all routes grouped by status
    return (
      <>
        {/* Active Routes */}
        {activeRoutes.map((route: any, index: number) => {
          const hasWhatsAppLink = !!route.meta?.contact?.groupLink;
          return (
            <div key={`active-${route.id || index}`} className="route-info-tile route-info-tile--card" style={{ marginBottom: activeRoutes.length > 1 && index < activeRoutes.length - 1 ? '16px' : '0' }}>
              <div className="route-info-tile__header">
                <span className="route-info-tile__badge">
                  🚌 ROUTE ASSIGNED
                </span>
              </div>

              <h3 className="route-info-tile__route-name">{route.name}</h3>

              {hasWhatsAppLink ? (
                <a
                  href={route.meta.contact.groupLink}
                  target="_blank"
                  rel="noreferrer"
                  className="route-info-tile__whatsapp-link"
                >
                  <WhatsAppOutlined className="route-info-tile__whatsapp-icon" />
                  Join WhatsApp Group
                </a>
              ) : (
                <div className="route-info-tile__whatsapp-unavailable">
                  <WhatsAppOutlined className="route-info-tile__whatsapp-icon" />
                  WhatsApp Group not available
                </div>
              )}
            </div>
          );
        })}

        {/* Planning Routes */}
        {planningRoutes.map((route: any, index: number) => (
          <div key={`planning-${route.id || index}`} className="route-info-tile route-info-tile--card route-info-tile--planning" style={{ marginBottom: index < planningRoutes.length - 1 ? '16px' : (hasActiveRoutes || hasInactiveRoutes) ? '16px' : '0', marginTop: hasActiveRoutes && index === 0 ? '16px' : '0' }}>
            <div className="route-info-tile__header">
              <span className="route-info-tile__badge route-info-tile__badge--planning">
                <ClockCircleOutlined /> ROUTE BEING PLANNED
              </span>
            </div>
            <h3 className="route-info-tile__route-name" style={{ fontSize: '18px', marginBottom: '8px' }}>{route.name}</h3>
            <p className="route-info-tile__planning-msg">
              This route is currently being planned. We'll notify you as soon as it is activated. Once active, you can view route details and join the group from here.
            </p>
          </div>
        ))}

        {/* Inactive Routes */}
        {inactiveRoutes.map((route: any, index: number) => (
          <div key={`inactive-${route.id || index}`} className="route-info-tile route-info-tile--card route-info-tile--inactive" style={{ marginBottom: index < inactiveRoutes.length - 1 ? '16px' : '0', marginTop: (hasActiveRoutes || hasPlanningRoutes) && index === 0 ? '16px' : '0' }}>
            <div className="route-info-tile__header">
              <span className="route-info-tile__badge route-info-tile__badge--inactive">
                <InfoCircleOutlined /> ROUTE INACTIVE
              </span>
            </div>
            <h3 className="route-info-tile__route-name" style={{ fontSize: '18px', marginBottom: '8px' }}>{route.name}</h3>
            <p className="route-info-tile__inactive-msg">
              This route is currently inactive. We'll notify you the details of the updated route. Please feel free to contact the support team via the helpline number.
            </p>
          </div>
        ))}
      </>
    );
  }

  // Banner variant for dashboard - compact style like payment banner
  // Don't show banner if loading or no routes found
  if (loading || routes.length === 0) {
    return null;
  }

  // For banner, show multiple routes stacked
  return (
    <>
      {/* Active Routes */}
      {activeRoutes.map((route: any, index: number) => {
        const hasWhatsAppLink = !!route.meta?.contact?.groupLink;
        return (
          <div key={`banner-active-${route.id || index}`} className="route-info-tile route-info-tile--banner" style={{ marginTop: index > 0 ? '8px' : '0' }}>
            <span className="route-info-tile__icon">🚌</span>
            <div className="route-info-tile__content">
              <span className="route-info-tile__title">Route Assigned</span>
              <span className="route-info-tile__subtitle">{route.name}</span>
            </div>
            {hasWhatsAppLink ? (
              <a
                href={route.meta?.contact?.groupLink}
                target="_blank"
                rel="noreferrer"
                className="route-info-tile__join-btn"
              >
                <WhatsAppOutlined style={{ fontSize: 16 }} /> Join
              </a>
            ) : (
              <button
                className="route-info-tile__join-btn route-info-tile__join-btn--disabled"
                disabled
              >
                <WhatsAppOutlined style={{ fontSize: 16 }} /> N/A
              </button>
            )}
          </div>
        );
      })}

      {/* Planning Routes */}
      {planningRoutes.map((route: any, index: number) => (
        <div key={`banner-planning-${route.id || index}`} className="route-info-tile route-info-tile--banner route-info-tile--banner-planning" style={{ marginTop: hasActiveRoutes || index > 0 ? '8px' : '0' }}>
          <ClockCircleOutlined className="route-info-tile__icon" style={{ fontSize: 18 }} />
          <div className="route-info-tile__content">
            <span className="route-info-tile__title">Route Being Planned</span>
            <span className="route-info-tile__subtitle">
              {planningRoutes.length > 1 ? `${route.name} - ` : ''}Your route is currently being planned. We'll notify you once it's activated.
            </span>
          </div>
        </div>
      ))}

      {/* Inactive Routes */}
      {inactiveRoutes.map((route: any, index: number) => (
        <div key={`banner-inactive-${route.id || index}`} className="route-info-tile route-info-tile--banner route-info-tile--banner-inactive" style={{ marginTop: (hasActiveRoutes || hasPlanningRoutes) || index > 0 ? '8px' : '0' }}>
          <InfoCircleOutlined className="route-info-tile__icon" style={{ fontSize: 18 }} />
          <div className="route-info-tile__content">
            <span className="route-info-tile__title">Route Inactive</span>
            <span className="route-info-tile__subtitle">
              {inactiveRoutes.length > 1 ? `${route.name} - ` : ''}Your route is currently inactive. Please contact support for details.
            </span>
          </div>
        </div>
      ))}
    </>
  );
};
